import { useMemo } from 'react';
import { Grid, type SxProps, type Theme } from '@mui/material';

import { ElementType } from '../../parsing/parser';
import type {
  FormFieldDef,
  FormFieldElementSchema,
  FormSchema,
  FormSectionElementSchema,
} from '../../types';
import { DraftAwareFieldControl } from './DraftAwareFieldControl';
import { useSectionDecorator } from '../../context/SectionDraftContext';
import { LegacyFieldControl } from './legacy/LegacyFieldControl';
import { Section } from './Section';

import { withFormController } from '../../../components/hocs/forms';
import type { ProductType } from '../../../types';
import { isLegacyStyleFormProduct } from './helpers/form-layout-utils';
import { collectFieldIds } from '../../utils/fieldIds';

type ElementProps = {
  fields: FormSchema['fields'];
  values: FormSchema['values'];
  gridSize?: number;
  isSectionChild?: boolean;
  productType?: ProductType;
  disabled?: boolean;
  elementLabelSxProps?: SxProps<Theme>;
  showConfidence?: boolean;
  formId: string;
  // RAG props
  enableRag?: boolean;
  submissionId?: string;
} & (FormSectionElementSchema | FormFieldElementSchema);

export const Element = (props: ElementProps) => {
  const elementSizeForGrid = props.gridSize ?? MAX_GRID_COLUMNS;
  const decorateSection = useSectionDecorator();

  // Memoised on the schema, which is stable per query result. Without it the
  // whole section subtree is re-walked on every render — once per keystroke on
  // a form with hundreds of fields — and the fresh array defeats any memo
  // downstream of the decorator.
  const sectionElements =
    props.type == ElementType.Section ? props.elements : undefined;
  const sectionFieldIds = useMemo(
    () =>
      sectionElements ? collectFieldIds(sectionElements) : EMPTY_FIELD_IDS,
    [sectionElements]
  );

  // Top-level sections only. A parent's field list already contains its
  // subsections', so decorating both would stack a second set of controls
  // inside the group the first one covers.
  const sectionActions =
    props.type == ElementType.Section &&
    !props.isSectionChild &&
    decorateSection
      ? decorateSection({
          title: props.title,
          fieldIds: sectionFieldIds,
        })
      : undefined;

  if (props.type == ElementType.Section) {
    const childrenElementSizeWithinGrid = getElementGridItemSize(
      props.metadata?.numColumns
    );

    const gridSizeOverride =
      props.metadata?.gridSize === 0
        ? childrenElementSizeWithinGrid
        : props.metadata?.gridSize;

    return (
      <Section
        title={props.title}
        conditions={props.conditions}
        gridSize={elementSizeForGrid}
        isSubsection={props.isSectionChild}
        actions={sectionActions}
        fieldIds={sectionFieldIds}
      >
        {props.elements.map((el) => {
          return (
            <Element
              key={el.type == ElementType.Field ? el.fieldId : el.title}
              {...el}
              fields={props.fields}
              values={props.values}
              formId={props.formId}
              gridSize={gridSizeOverride}
              isSectionChild
              productType={props.productType}
              disabled={props.disabled}
              elementLabelSxProps={props.elementLabelSxProps}
              showConfidence={props.showConfidence}
              enableRag={props.enableRag}
              submissionId={props.submissionId}
            />
          );
        })}
      </Section>
    );
  }

  if (props.type == ElementType.Field) {
    const fieldDef = props.fields[props.fieldId];
    if (!fieldDef) {
      throw new Error(`No field definition for field ID '${props.fieldId}'`);
    }

    if (props.productType && isLegacyStyleFormProduct(props.productType)) {
      return (
        <Grid item sm={elementSizeForGrid}>
          <ControlledLegacyFieldControl
            name={fieldDef.id}
            label={fieldDef.text}
            meta={fieldDef}
            conditions={fieldDef?.conditions}
            disabled={isFieldDisabled(props, fieldDef)}
            confidenceScore={fieldDef?.confidenceScore}
            showConfidence={props.showConfidence}
            formId={props.formId}
            enableRag={props.enableRag}
            submissionId={props.submissionId}
          />
        </Grid>
      );
    }

    return (
      <Grid item sm={elementSizeForGrid}>
        <ControlledFieldControl
          name={fieldDef.id}
          label={fieldDef.text}
          meta={fieldDef}
          conditions={fieldDef?.conditions}
          disabled={isFieldDisabled(props, fieldDef)}
          elementLabelSxProps={props.elementLabelSxProps}
          confidenceScore={fieldDef?.confidenceScore}
          showConfidence={props.showConfidence}
          formId={props.formId}
          enableRag={props.enableRag}
          submissionId={props.submissionId}
        />
      </Grid>
    );
  }

  return null;
};

// Wrapped in the draft seam rather than FieldControl directly. Inert unless a
// `FieldDraftProvider` is installed above the form (Risk Data is the only one
// today), so every other form renders exactly as before.
export const ControlledFieldControl = withFormController(
  DraftAwareFieldControl
);
const ControlledLegacyFieldControl = withFormController(LegacyFieldControl);

/** Every field id in a section's subtree, in render order. */
const EMPTY_FIELD_IDS: readonly string[] = [];

const MAX_GRID_COLUMNS = 12;
const getElementGridItemSize = (columns: number | undefined) => {
  return columns != null ? MAX_GRID_COLUMNS / columns : MAX_GRID_COLUMNS;
};
function isFieldDisabled(props: ElementProps, fieldDef: FormFieldDef): boolean {
  return (props.disabled || fieldDef?.schema?.disabled) ?? false;
}
